import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import {
  AlertTriangle, Plus, Search, List, Columns3, X, ChevronRight,
  Clock, User, MessageSquare, FileText, History, Send, Loader2,
  AlertCircle, CheckCircle, ArrowRight, Timer, UserCheck,
  ChevronLeft, StickyNote, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const font = { barlow: { fontFamily: 'Barlow Condensed, Barlow, sans-serif' }, mono: { fontFamily: 'JetBrains Mono, monospace' } };

/* ─── Status & Priority Config ───────────────────────────── */
const STATUS_MAP = {
  nouveau: { label: 'Nouveau', bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
  ouvert: { label: 'Ouvert', bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
  open: { label: 'Ouvert', bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
  en_cours: { label: 'En cours', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
  en_attente_client: { label: 'Attente client', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  attente_client: { label: 'Attente client', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
  en_attente_interne: { label: 'Attente interne', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', dot: 'bg-orange-400' },
  resolu: { label: 'Resolu', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  clos: { label: 'Clos', bg: 'bg-zinc-100', text: 'text-zinc-500', border: 'border-zinc-200', dot: 'bg-zinc-400' },
};
const SEVERITY_MAP = {
  critique: { label: 'P1', bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', stripe: 'bg-red-500' },
  elevee: { label: 'P2', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', stripe: 'bg-orange-400' },
  moyenne: { label: 'P3', bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', stripe: 'bg-slate-400' },
  faible: { label: 'P4', bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200', stripe: 'bg-slate-300' },
  low: { label: 'P4', bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200', stripe: 'bg-slate-300' },
};
const PRIORITY_MAP = {
  haute: { label: 'Haute', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  normale: { label: 'Normale', bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200' },
  basse: { label: 'Basse', bg: 'bg-slate-50', text: 'text-slate-500', border: 'border-slate-200' },
};
const STATUS_FLOW = {
  nouveau: ['en_cours'],
  ouvert: ['en_cours'],
  open: ['en_cours'],
  en_cours: ['en_attente_client', 'resolu'],
  en_attente_client: ['en_cours', 'resolu'],
  attente_client: ['en_cours', 'resolu'],
  en_attente_interne: ['en_cours', 'resolu'],
  resolu: ['clos', 'en_cours'],
  clos: [],
};
const KANBAN_COLS = ['nouveau', 'en_cours', 'en_attente_client', 'resolu', 'clos'];
const TABS = [
  { key: 'all', label: 'Tous' },
  { key: 'nouveau', label: 'Nouveaux' },
  { key: 'en_cours', label: 'En cours' },
  { key: 'en_attente_client', label: 'Attente' },
  { key: 'non_assignes', label: 'Non assignes' },
  { key: 'sla_depasse', label: 'SLA depasse' },
  { key: 'mes_incidents', label: 'Mes incidents' },
];

/* ─── SLA Timer ─────────────────────────────────────────── */
const SLATimer = ({ incident }) => {
  const [elapsed, setElapsed] = useState(0);
  const slaMinutes = incident.slaDeadlineMinutes || 240;
  useEffect(() => {
    if (!incident.createdAt || ['resolu', 'clos'].includes(incident.status)) return;
    const start = new Date(incident.createdAt).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - start) / 60000));
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [incident.createdAt, incident.status, slaMinutes]);
  if (['resolu', 'clos'].includes(incident.status)) return null;
  const pct = Math.min((elapsed / slaMinutes) * 100, 100);
  const remaining = slaMinutes - elapsed;
  const overdue = remaining < 0;
  const h = Math.floor(Math.abs(remaining) / 60);
  const m = Math.abs(remaining) % 60;
  return (
    <div className="flex items-center gap-2" data-testid="sla-timer">
      <Timer className={`w-3.5 h-3.5 ${overdue ? 'text-red-500' : pct > 75 ? 'text-amber-500' : 'text-slate-400'}`} />
      <div className="flex-1">
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden w-16">
          <div className={`h-full rounded-full transition-all ${overdue ? 'bg-red-500' : pct > 75 ? 'bg-amber-400' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className={`text-[10px] font-medium ${overdue ? 'text-red-600' : 'text-slate-500'}`} style={font.mono}>
        {overdue ? `-${h}h${String(m).padStart(2, '0')}` : `${h}h${String(m).padStart(2, '0')}`}
      </span>
    </div>
  );
};

/* ─── KPI Card ──────────────────────────────────────────── */
const KPICard = ({ icon: Icon, label, value, color, pulse }) => (
  <Card className="relative overflow-hidden hover:shadow-md transition-shadow duration-300" data-testid={`kpi-${label.toLowerCase().replace(/ /g, '-')}`}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
          <p className="text-3xl font-bold text-slate-900 mt-1.5 tracking-tight" style={font.barlow}>{value}</p>
        </div>
        <div className={`p-2.5 rounded-lg ${color} relative`}>
          <Icon className="w-5 h-5" />
          {pulse && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-ping" />}
          {pulse && <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full" />}
        </div>
      </div>
    </CardContent>
  </Card>
);

/* ─── Incident Row (Card Style) ────────────────────────── */
const IncidentRow = ({ incident, onClick, isActive }) => {
  const sev = SEVERITY_MAP[incident.severity] || SEVERITY_MAP.moyenne;
  const st = STATUS_MAP[incident.status] || STATUS_MAP.nouveau;
  const cause = incident.cause || incident.title || 'Sans titre';
  const description = incident.description || '';
  const refNumber = incident.incident_id || incident.referenceNumber || `INC-${incident.id?.slice(0, 6)}`;
  const assignedTo = incident.assigned_to_name || incident.assignedToName || null;
  const clientName = incident.client_name || incident.clientName || incident.client_email || null;
  const updatedAt = incident.updated_at || incident.updatedAt || incident.created_at || incident.createdAt;
  const typeLabel = incident.incident_type_label || incident.incident_type || '';
  return (
    <div
      onClick={() => onClick(incident)}
      className={`group relative flex items-start gap-3 p-4 rounded-lg border bg-white cursor-pointer transition-all duration-200
        hover:border-slate-300 hover:shadow-sm
        ${isActive ? 'border-slate-400 shadow-sm ring-1 ring-slate-300/50' : 'border-slate-200'}`}
      data-testid={`incident-row-${incident.id}`}
    >
      {/* Thin severity indicator */}
      <div className={`absolute left-0 top-3 bottom-3 w-0.5 rounded-full ${sev.stripe}`} />
      {/* Content */}
      <div className="flex-1 min-w-0 pl-2">
        {/* Row 1: Ref + Type + Badges */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[10px] font-semibold text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded" style={font.mono}>
            {refNumber}
          </span>
          {typeLabel && <span className="text-[10px] text-slate-400">{typeLabel}</span>}
          <div className="flex items-center gap-1 ml-auto shrink-0">
            <Badge className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0 ${sev.bg} ${sev.text} border ${sev.border}`}>
              {sev.label}
            </Badge>
            <Badge className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0 ${st.bg} ${st.text} border ${st.border}`}>
              {st.label}
            </Badge>
          </div>
        </div>
        {/* Row 2: Cause/Title */}
        <p className="text-sm font-medium text-slate-800 line-clamp-1">{cause}</p>
        {/* Row 3: Description preview */}
        {description && (
          <p className="text-xs text-slate-400 line-clamp-1 mt-0.5">{description}</p>
        )}
        {/* Row 4: Metadata */}
        <div className="flex items-center gap-4 mt-2 flex-wrap">
          {clientName && (
            <span className="flex items-center gap-1 text-[11px] text-slate-400" data-testid="incident-client">
              <User className="w-3 h-3" /> {clientName}
            </span>
          )}
          {assignedTo && (
            <span className="flex items-center gap-1 text-[11px] text-slate-400" data-testid="incident-assigned">
              <UserCheck className="w-3 h-3" /> {assignedTo}
            </span>
          )}
          {!assignedTo && (
            <span className="flex items-center gap-1 text-[11px] text-slate-300 italic" data-testid="incident-unassigned">
              <UserCheck className="w-3 h-3" /> Non assigne
            </span>
          )}
          {updatedAt && (
            <span className="flex items-center gap-1 text-[11px] text-slate-400 ml-auto" style={font.mono} data-testid="incident-updated">
              <Clock className="w-3 h-3" /> MAJ {new Date(updatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 transition-colors shrink-0 mt-1" />
    </div>
  );
};

/* ─── Kanban Card ───────────────────────────────────────── */
const KanbanCard = ({ incident, onClick }) => {
  const sev = SEVERITY_MAP[incident.severity] || SEVERITY_MAP.moyenne;
  const cause = incident.cause || incident.title || 'Sans titre';
  const refNumber = incident.incident_id || incident.referenceNumber || `INC-${incident.id?.slice(0, 6)}`;
  const assignedTo = incident.assigned_to_name || incident.assignedToName || null;
  return (
    <div
      onClick={() => onClick(incident)}
      className="group p-3 rounded-lg border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm cursor-pointer transition-all duration-200"
      data-testid={`kanban-card-${incident.id}`}
    >
      <div className="flex items-start gap-2">
        <div className={`w-0.5 h-8 rounded-full shrink-0 mt-0.5 ${sev.stripe}`} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-slate-800 line-clamp-2">{cause}</p>
          <p className="text-[10px] text-slate-400 mt-0.5 truncate" style={font.mono}>
            {refNumber}
          </p>
          {assignedTo && (
            <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1">
              <UserCheck className="w-2.5 h-2.5" /> {assignedTo}
            </p>
          )}
        </div>
        <Badge className={`text-[8px] font-bold px-1.5 py-0 ${sev.bg} ${sev.text} border ${sev.border} shrink-0`}>
          {sev.label}
        </Badge>
      </div>
    </div>
  );
};

/* ─── Kanban Board ──────────────────────────────────────── */
const KanbanBoard = ({ incidents, onSelect }) => (
  <div className="grid grid-cols-5 gap-3 min-h-[400px]" data-testid="kanban-board">
    {KANBAN_COLS.map(col => {
      const st = STATUS_MAP[col];
      const items = incidents.filter(i => i.status === col);
      return (
        <div key={col} className="flex flex-col rounded-xl border border-slate-200 bg-slate-50/50 overflow-hidden">
          <div className={`px-3 py-2.5 border-b flex items-center gap-2 ${st.bg}`}>
            <div className={`w-2 h-2 rounded-full ${st.dot}`} />
            <span className={`text-xs font-bold uppercase tracking-wider ${st.text}`}>{st.label}</span>
            <span className="text-[10px] text-slate-400 ml-auto font-medium">{items.length}</span>
          </div>
          <ScrollArea className="flex-1 p-2">
            <div className="space-y-2">
              {items.map(inc => <KanbanCard key={inc.id} incident={inc} onClick={onSelect} />)}
              {items.length === 0 && <p className="text-[10px] text-slate-400 text-center py-8">Aucun</p>}
            </div>
          </ScrollArea>
        </div>
      );
    })}
  </div>
);

/* ─── Status Pipeline ───────────────────────────────────── */
const PipelineStepper = ({ current }) => {
  const steps = KANBAN_COLS;
  const idx = steps.indexOf(current);
  return (
    <div className="flex items-center gap-1" data-testid="pipeline-stepper">
      {steps.map((s, i) => {
        const st = STATUS_MAP[s];
        const done = i <= idx;
        return (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider
              ${done ? `${st.bg} ${st.text}` : 'bg-slate-50 text-slate-300'}`}>
              {done && i < idx && <CheckCircle className="w-3 h-3" />}
              {done && i === idx && <div className={`w-2 h-2 rounded-full ${st.dot}`} />}
              {st.label}
            </div>
            {i < steps.length - 1 && <ArrowRight className={`w-3 h-3 ${done ? 'text-slate-400' : 'text-slate-200'}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ─── Detail Panel ──────────────────────────────────────── */
const DetailPanel = ({
  incident, onClose, messages, onSendMessage, internalNotes, onAddNote,
  onTransition, onAssign, onCloseIncident, agents,
}) => {
  const [activeTab, setActiveTab] = useState('conversation');
  const [msgText, setMsgText] = useState('');
  const [noteText, setNoteText] = useState('');
  const [sending, setSending] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    const incId = incident?.incident_id || incident?.id;
    if (incId) {
      axios.get(`${API}/incidents/${incId}/timeline`, {})
        .then(r => setTimeline(r.data?.timeline || r.data || []))
        .catch(() => {});
    }
  }, [incident?.incident_id, incident?.id]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  if (!incident) return null;
  const sev = SEVERITY_MAP[incident.severity] || SEVERITY_MAP.moyenne;
  const st = STATUS_MAP[incident.status] || STATUS_MAP.nouveau;
  const nextStatuses = STATUS_FLOW[incident.status] || [];
  const cause = incident.cause || incident.title || 'Sans titre';
  const refNumber = incident.incident_id || incident.referenceNumber || `INC-${incident.id?.slice(0, 6)}`;

  const handleSend = async () => {
    if (!msgText.trim()) return;
    setSending(true);
    await onSendMessage(msgText.trim());
    setMsgText('');
    setSending(false);
  };
  const handleNote = async () => {
    if (!noteText.trim()) return;
    setSending(true);
    await onAddNote(noteText.trim());
    setNoteText('');
    setSending(false);
  };

  const tabs = [
    { key: 'conversation', label: 'Conversation', icon: MessageSquare },
    { key: 'notes', label: 'Notes', icon: StickyNote },
    { key: 'details', label: 'Details', icon: FileText },
    { key: 'timeline', label: 'Historique', icon: History },
  ];

  return (
    <div className="w-full md:w-[500px] lg:w-[580px] border-l border-slate-200 bg-white shadow-2xl flex flex-col h-full z-30" data-testid="incident-detail-panel">
      {/* Header - Glassmorphism */}
      <div className="p-5 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-10">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1.5">
              <Badge className={`text-[9px] font-bold uppercase ${sev.bg} ${sev.text} border ${sev.border}`}>{sev.label}</Badge>
              <Badge className={`text-[9px] font-bold uppercase ${st.bg} ${st.text} border ${st.border}`}>{st.label}</Badge>
            </div>
            <h2 className="text-lg font-bold text-slate-900 tracking-tight leading-tight" style={font.barlow}>{cause}</h2>
            <p className="text-[10px] text-slate-400 mt-1" style={font.mono}>
              {refNumber} · {incident.created_at || incident.createdAt ? new Date(incident.created_at || incident.createdAt).toLocaleDateString('fr-FR') : ''}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="shrink-0 hover:bg-slate-100" data-testid="close-detail-panel">
            <X className="w-5 h-5" />
          </Button>
        </div>
        {/* Pipeline */}
        <div className="mt-3"><PipelineStepper current={incident.status} /></div>
        {/* Actions */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {nextStatuses.map(ns => (
            <Button key={ns} size="sm" variant="outline" onClick={() => onTransition(incident, ns)}
              className="text-xs h-7 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300" data-testid={`transition-${ns}`}>
              <ArrowRight className="w-3 h-3 mr-1" /> {STATUS_MAP[ns]?.label}
            </Button>
          ))}
          {!['clos'].includes(incident.status) && (
            <Button size="sm" variant="outline" onClick={() => onCloseIncident(incident)}
              className="text-xs h-7 text-red-600 hover:bg-red-50 hover:border-red-300 ml-auto" data-testid="close-incident-btn">
              Cloturer
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 px-5 gap-1 bg-slate-50/50">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium border-b-2 transition-colors
              ${activeTab === t.key ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${t.key}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 flex flex-col min-h-0">
        {activeTab === 'conversation' && (
          <div className="flex flex-col flex-1 min-h-0">
            <ScrollArea className="flex-1 p-5" ref={scrollRef}>
              <div className="space-y-3">
                {(!messages || messages.length === 0) && (
                  <p className="text-xs text-slate-400 text-center py-8">Aucun message</p>
                )}
                {messages?.map((msg, i) => (
                  <div key={i} className={`flex ${msg.sender_role !== 'client' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] px-3.5 py-2.5 rounded-xl text-sm ${
                      msg.sender_role !== 'client' ? 'bg-slate-700 text-white rounded-br-sm' : 'bg-slate-100 text-slate-800 rounded-bl-sm'
                    }`}>
                      <p>{msg.content || msg.message}</p>
                      <p className={`text-[9px] mt-1 ${msg.sender_role !== 'client' ? 'text-slate-400' : 'text-slate-400'}`} style={font.mono}>
                        {msg.sender_name || msg.senderName || msg.sender || ''} · {msg.created_at || msg.createdAt ? new Date(msg.created_at || msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input value={msgText} onChange={e => setMsgText(e.target.value)} placeholder="Ecrire un message..."
                  className="text-sm" onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()} data-testid="message-input" />
                <Button onClick={handleSend} disabled={sending || !msgText.trim()} size="icon"
                  className="bg-emerald-600 hover:bg-emerald-700 shrink-0" data-testid="send-message-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="flex flex-col flex-1 min-h-0">
            <ScrollArea className="flex-1 p-5">
              <div className="space-y-3">
                {(!internalNotes || internalNotes.length === 0) && (
                  <p className="text-xs text-slate-400 text-center py-8">Aucune note interne</p>
                )}
                {internalNotes?.map((note, i) => (
                  <div key={i} className="p-3 rounded-lg bg-amber-50/70 border border-amber-200/50">
                    <p className="text-sm text-slate-800">{note.content || note.note}</p>
                    <p className="text-[9px] text-amber-600 mt-1.5" style={font.mono}>
                      {note.sender_name || note.authorName || note.author || 'Agent'} · {(note.created_at || note.createdAt) ? new Date(note.created_at || note.createdAt).toLocaleDateString('fr-FR') : ''}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollArea>
            <div className="p-4 border-t border-slate-200 bg-white">
              <div className="flex gap-2">
                <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Note interne..."
                  className="text-sm" onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleNote()} data-testid="note-input" />
                <Button onClick={handleNote} disabled={sending || !noteText.trim()} size="icon" variant="outline" className="shrink-0" data-testid="add-note-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <StickyNote className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'details' && (
          <ScrollArea className="flex-1 p-5">
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Description</p>
                <p className="text-sm text-slate-700 leading-relaxed">{incident.description || incident.cause || 'Aucune description'}</p>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Severite', value: sev.label, badge: true, cls: `${sev.bg} ${sev.text} ${sev.border}` },
                  { label: 'Priorite', value: PRIORITY_MAP[incident.priority]?.label || incident.priority || 'N/A', badge: true, cls: `${PRIORITY_MAP[incident.priority]?.bg || 'bg-slate-100'} ${PRIORITY_MAP[incident.priority]?.text || 'text-slate-600'} ${PRIORITY_MAP[incident.priority]?.border || 'border-slate-200'}` },
                  { label: 'Type', value: incident.incident_type_label || incident.category || 'N/A' },
                  { label: 'Assigne a', value: incident.assigned_to_name || incident.assignedToName || 'Non assigne' },
                  { label: 'Client', value: incident.client_name || incident.clientName || incident.client_email || incident.clientEmail || 'N/A' },
                  { label: 'Operation', value: incident.operation_reference || incident.operationRef || incident.operation_id?.slice(0, 8) || 'N/A' },
                  { label: 'Lieu', value: incident.location || 'N/A' },
                  { label: 'Cree le', value: (incident.created_at || incident.createdAt) ? new Date(incident.created_at || incident.createdAt).toLocaleDateString('fr-FR') : 'N/A', mono: true },
                  { label: 'Derniere MAJ', value: (incident.updated_at || incident.updatedAt) ? new Date(incident.updated_at || incident.updatedAt).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : 'N/A', mono: true },
                ].map((item, i) => (
                  <div key={i} className="space-y-0.5">
                    <p className="text-[10px] font-medium text-slate-400 uppercase">{item.label}</p>
                    {item.badge ? (
                      <Badge className={`text-[10px] font-bold border ${item.cls}`}>{item.value}</Badge>
                    ) : (
                      <p className={`text-sm font-medium text-slate-800 ${item.mono ? '' : ''}`} style={item.mono ? font.mono : undefined}>{item.value}</p>
                    )}
                  </div>
                ))}
              </div>
              {/* Assign agent */}
              {agents && agents.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Assigner</p>
                    <Select value={incident.assigned_to || incident.assignedTo || ''} onValueChange={v => onAssign(incident, v)}>
                      <SelectTrigger className="text-sm" data-testid="assign-select"><SelectValue placeholder="Choisir un agent" /></SelectTrigger>
                      <SelectContent>
                        {agents.map(a => (
                          <SelectItem key={a.id} value={a.id}>{a.name || `${a.firstName} ${a.lastName}`}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </ScrollArea>
        )}

        {activeTab === 'timeline' && (
          <ScrollArea className="flex-1 p-5">
            <div className="space-y-0">
              {(!timeline || timeline.length === 0) && (
                <p className="text-xs text-slate-400 text-center py-8">Aucun historique</p>
              )}
              {timeline?.map((ev, i) => (
                <div key={i} className="flex gap-3 pb-4 relative">
                  {i < timeline.length - 1 && <div className="absolute left-[9px] top-5 bottom-0 w-px bg-slate-200" />}
                  <div className={`w-[18px] h-[18px] rounded-full border-2 mt-0.5 shrink-0 flex items-center justify-center
                    ${ev.type === 'status_change' ? 'border-blue-400 bg-blue-50' :
                      ev.type === 'message' ? 'border-emerald-400 bg-emerald-50' :
                      'border-slate-300 bg-slate-50'}`}>
                    <div className={`w-1.5 h-1.5 rounded-full
                      ${ev.type === 'status_change' ? 'bg-blue-500' :
                        ev.type === 'message' ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-700">{ev.description || ev.action}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5" style={font.mono}>
                      {ev.user_name || ev.userName || ev.user || ''} · {(ev.timestamp || ev.createdAt) ? new Date(ev.timestamp || ev.createdAt).toLocaleDateString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                    {ev.note && <p className="text-[10px] text-slate-500 mt-1 italic">"{ev.note}"</p>}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </div>
    </div>
  );
};

/* ─── Status Transition Dialog ──────────────────────────── */
const TransitionDialog = ({ open, onOpenChange, incident, targetStatus, onConfirm }) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const targetSt = STATUS_MAP[targetStatus] || {};
  const handleConfirm = async () => {
    if (!note.trim()) return;
    setLoading(true);
    await onConfirm(note.trim());
    setNote('');
    setLoading(false);
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px] sm:rounded-xl" data-testid="transition-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold tracking-tight" style={font.barlow}>Changer le statut</DialogTitle>
          <DialogDescription>
            Vers: <Badge className={`text-[10px] font-bold ${targetSt.bg} ${targetSt.text} border ${targetSt.border} ml-1`}>{targetSt.label}</Badge>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Note obligatoire *</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={3} placeholder="Raison du changement..." data-testid="transition-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleConfirm} disabled={loading || !note.trim()}
            className="bg-emerald-600 hover:bg-emerald-700" data-testid="transition-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ─── Close Incident (Sheet for P1/P2, Dialog for P3/P4) ── */
const CloseSheetContent = ({ incident, form, setForm, loading, onConfirm, onCancel }) => {
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: typeof e === 'string' ? e : e.target.value }));
  const cause = incident?.cause || incident?.title || '';
  const refNumber = incident?.incident_id || `INC-${incident?.id?.slice(0, 6)}`;
  const sev = SEVERITY_MAP[incident?.severity] || SEVERITY_MAP.moyenne;

  return (
    <div className="space-y-6 p-1">
      {/* Incident Summary */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-slate-400 bg-white px-1.5 py-0.5 rounded" style={font.mono}>{refNumber}</span>
          <Badge className={`text-[9px] font-bold ${sev.bg} ${sev.text} border ${sev.border}`}>{sev.label}</Badge>
        </div>
        <p className="text-sm font-medium text-slate-800">{cause}</p>
        {incident?.description && <p className="text-xs text-slate-500">{incident.description}</p>}
      </div>

      {/* RCA Section */}
      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Analyse Cause Racine (RCA)</h3>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Cause racine identifiee *</Label>
            <Select value={form.rootCauseCategory} onValueChange={v => setForm(p => ({ ...p, rootCauseCategory: v }))}>
              <SelectTrigger data-testid="close-root-cause-category"><SelectValue placeholder="Categorie de cause" /></SelectTrigger>
              <SelectContent>
                {['erreur_humaine', 'defaut_materiel', 'probleme_processus', 'facteur_externe', 'non_identifiee'].map(c => (
                  <SelectItem key={c} value={c}>{
                    { erreur_humaine: 'Erreur humaine', defaut_materiel: 'Defaut materiel', probleme_processus: 'Probleme de processus', facteur_externe: 'Facteur externe', non_identifiee: 'Non identifiee' }[c]
                  }</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Description de la cause racine *</Label>
            <Textarea value={form.rootCause} onChange={set('rootCause')} rows={3} placeholder="Decrivez la cause profonde de l'incident..." data-testid="close-root-cause" />
          </div>
        </div>
      </div>

      {/* Resolution */}
      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Resolution</h3>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Resolution appliquee *</Label>
            <Textarea value={form.resolution} onChange={set('resolution')} rows={3} placeholder="Comment l'incident a été résolu…" data-testid="close-resolution" />
          </div>
        </div>
      </div>

      {/* Impact */}
      <div className="space-y-4">
        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Impact & Actions preventives</h3>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Evaluation de l'impact</Label>
            <Select value={form.impactLevel} onValueChange={v => setForm(p => ({ ...p, impactLevel: v }))}>
              <SelectTrigger data-testid="close-impact-level"><SelectValue placeholder="Niveau d'impact" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aucun">Aucun impact client</SelectItem>
                <SelectItem value="mineur">Impact mineur</SelectItem>
                <SelectItem value="modere">Impact modere</SelectItem>
                <SelectItem value="majeur">Impact majeur</SelectItem>
                <SelectItem value="critique">Impact critique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Actions preventives</Label>
            <Textarea value={form.preventiveActions} onChange={set('preventiveActions')} rows={3} placeholder="Quelles mesures pour eviter que ca se reproduise..." data-testid="close-preventive" />
          </div>
        </div>
      </div>

      {/* Final note */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Note de cloture *</Label>
        <Textarea value={form.note} onChange={set('note')} rows={2} placeholder="Note finale..." data-testid="close-note" />
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button variant="outline" className="flex-1" onClick={onCancel}>Annuler</Button>
        <Button
          onClick={onConfirm}
          disabled={loading || !form.resolution.trim() || !form.rootCause.trim() || !form.note.trim()}
          className="flex-1 bg-red-600 hover:bg-red-700 text-white"
          data-testid="close-confirm-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
          Cloturer l'incident
        </Button>
      </div>
    </div>
  );
};

const CloseDialog = ({ open, onOpenChange, incident, onConfirm }) => {
  const [form, setForm] = useState({ resolution: '', rootCause: '', rootCauseCategory: '', note: '', impactLevel: '', preventiveActions: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const isHighSeverity = incident?.severity === 'critique' || incident?.severity === 'elevee';

  const handleConfirm = async () => {
    if (isHighSeverity) {
      if (!form.resolution.trim() || !form.rootCause.trim() || !form.note.trim()) return;
    } else {
      if (!form.resolution.trim() || !form.note.trim()) return;
    }
    setLoading(true);
    await onConfirm(form);
    setForm({ resolution: '', rootCause: '', rootCauseCategory: '', note: '', impactLevel: '', preventiveActions: '' });
    setLoading(false);
    onOpenChange(false);
  };

  const handleCancel = () => onOpenChange(false);

  // P1/P2: Use Sheet for detailed RCA
  if (isHighSeverity) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="sm:max-w-[540px] overflow-y-auto" data-testid="close-incident-sheet">
          <SheetHeader className="mb-4">
            <SheetTitle className="text-lg font-bold tracking-tight" style={font.barlow}>
              Cloture detaillee - Incident {SEVERITY_MAP[incident?.severity]?.label}
            </SheetTitle>
            <SheetDescription>
              Les incidents de severite haute necessitent une analyse détaillée.
            </SheetDescription>
          </SheetHeader>
          <CloseSheetContent
            incident={incident}
            form={form}
            setForm={setForm}
            loading={loading}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        </SheetContent>
      </Sheet>
    );
  }

  // P3/P4: Simple dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] sm:rounded-xl" data-testid="close-incident-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold tracking-tight" style={font.barlow}>Cloturer l'incident</DialogTitle>
          <DialogDescription>{incident?.cause || incident?.title || ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Resolution *</Label>
            <Textarea value={form.resolution} onChange={set('resolution')} rows={2} placeholder="Comment a été résolu…" data-testid="close-resolution" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Cause racine</Label>
            <Input value={form.rootCause} onChange={set('rootCause')} placeholder="Cause identifiee..." data-testid="close-root-cause" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Note de cloture *</Label>
            <Textarea value={form.note} onChange={set('note')} rows={2} placeholder="Note finale..." data-testid="close-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={handleConfirm} disabled={loading || !form.resolution.trim() || !form.note.trim()}
            className="bg-red-600 hover:bg-red-700 text-white" data-testid="close-confirm-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
            Cloturer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════ */
const IncidentsManagement = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [messages, setMessages] = useState([]);
  const [internalNotes, setInternalNotes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeIncident, setCloseIncident] = useState(null);
  const [transitionOpen, setTransitionOpen] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState(null);
  const [transitionIncident, setTransitionIncident] = useState(null);

  const headers = useMemo(() => ({ Authorization: 'Bearer cookie-auth' }), []);

  /* Fetch incidents */
  const fetchIncidents = useCallback(async () => {
    try {
      const params = { page, limit: 50 };
      if (activeTab === 'non_assignes') params.assigned_to = 'unassigned';
      else if (activeTab === 'sla_depasse') params.sla_overdue = true;
      else if (activeTab === 'mes_incidents') params.view = 'mes_incidents';
      else if (activeTab !== 'all') params.status = activeTab;
      if (searchQuery) params.search = searchQuery;
      const [incRes, statsRes] = await Promise.all([
        axios.get(`${API}/incidents`, { headers, params }),
        axios.get(`${API}/incidents/stats`, { headers }),
      ]);
      const data = incRes.data;
      setIncidents(data.incidents || data.items || data || []);
      setTotalPages(data.totalPages || data.total_pages || 1);
      setStats(statsRes.data);
    } catch (e) {
      console.error('Fetch incidents error:', e);
    }
    setLoading(false);
  }, [headers, page, activeTab, searchQuery]);

  useEffect(() => { fetchIncidents(); }, [fetchIncidents]);

  /* Fetch agents (staff interne pour assignation d'incidents) */
  useEffect(() => {
    axios.get(`${API}/internal-users`, { headers })
      .then(r => setAgents(r.data?.users || r.data || []))
      .catch(() => {});
  }, [headers]);

  /* Fetch messages & notes */
  const fetchMessages = useCallback(async (incidentId) => {
    if (!incidentId) return;
    try {
      const [msgRes, noteRes] = await Promise.all([
        axios.get(`${API}/incidents/${incidentId}/messages`, { headers }),
        axios.get(`${API}/incidents/${incidentId}/messages`, { headers, params: { include_internal: true } }).catch(() => ({ data: { messages: [] } })),
      ]);
      const msgs = msgRes.data?.messages || msgRes.data || [];
      setMessages(msgs.filter(m => !m.is_internal));
      const allMsgs = noteRes.data?.messages || noteRes.data || [];
      setInternalNotes(allMsgs.filter(m => m.is_internal));
    } catch { setMessages([]); setInternalNotes([]); }
  }, [headers]);

  useEffect(() => {
    if (selectedIncident) {
      const incId = selectedIncident.incident_id || selectedIncident.id;
      fetchMessages(incId);
    }
  }, [selectedIncident, fetchMessages]);

  /* Handlers */
  const handleTransition = (incident, targetStatus) => {
    setTransitionIncident(incident);
    setTransitionTarget(targetStatus);
    setTransitionOpen(true);
  };

  const confirmTransition = async (note) => {
    const incId = transitionIncident.incident_id || transitionIncident.id;
    try {
      await axios.put(`${API}/incidents/${incId}`, {
        status: transitionTarget
      }, { headers });
      toast({ title: `Statut mis a jour: ${STATUS_MAP[transitionTarget]?.label}` });
      fetchIncidents();
      if (selectedIncident?.id === transitionIncident.id) {
        setSelectedIncident(prev => ({ ...prev, status: transitionTarget }));
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur transition', variant: 'destructive' });
    }
  };

  const handleCloseIncident = (incident) => {
    setCloseIncident(incident);
    setCloseOpen(true);
  };

  const confirmClose = async (form) => {
    const incId = closeIncident.incident_id || closeIncident.id;
    try {
      await axios.post(`${API}/incidents/${incId}/close`, {
        resolution: form.resolution,
        resolution_reason: form.rootCauseCategory || form.rootCause || 'probleme_resolu',
        root_cause: form.rootCause,
        root_cause_category: form.rootCauseCategory,
        impact_level: form.impactLevel,
        preventive_actions: form.preventiveActions,
        final_message: form.note
      }, { headers });
      toast({ title: 'Incident cloture' });
      fetchIncidents();
      if (selectedIncident?.id === closeIncident.id) {
        setSelectedIncident(prev => ({ ...prev, status: 'clos' }));
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur cloture', variant: 'destructive' });
    }
  };

  const handleAssign = async (incident, agentId) => {
    const incId = incident.incident_id || incident.id;
    try {
      await axios.put(`${API}/incidents/${incId}/assign`, { assigned_to: agentId }, { headers });
      const agent = agents.find(a => a.id === agentId);
      toast({ title: `Assigne a ${agent?.name || agent?.firstName || 'Agent'}` });
      fetchIncidents();
      if (selectedIncident?.id === incident.id) {
        setSelectedIncident(prev => ({ ...prev, assigned_to: agentId, assigned_to_name: agent ? (agent.name || `${agent.firstName} ${agent.lastName}`) : '' }));
      }
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || 'Erreur assignation', variant: 'destructive' });
    }
  };

  const handleSendMessage = async (content) => {
    const incId = selectedIncident.incident_id || selectedIncident.id;
    try {
      await axios.post(`${API}/incidents/${incId}/messages`, { content, is_internal: false }, { headers });
      fetchMessages(incId);
    } catch (e) {
      toast({ title: 'Erreur envoi', variant: 'destructive' });
    }
  };

  const handleAddNote = async (content) => {
    const incId = selectedIncident.incident_id || selectedIncident.id;
    try {
      await axios.post(`${API}/incidents/${incId}/messages`, { content, is_internal: true }, { headers });
      fetchMessages(incId);
    } catch (e) {
      toast({ title: 'Erreur note', variant: 'destructive' });
    }
  };

  const selectIncident = (inc) => setSelectedIncident(inc);

  /* Stats derivation */
  const kpis = useMemo(() => {
    if (!stats) return { active: 0, urgent: 0, slaOverdue: 0, unassigned: 0 };
    const s = stats.staff_only || stats;
    return {
      active: stats.total || 0,
      urgent: s.urgents || s.urgent || 0,
      slaOverdue: s.sla_overdue || s.sla_depasse || 0,
      unassigned: s.non_assignes || s.unassigned || 0,
    };
  }, [stats]);

  return (
    <div className="flex flex-col h-full bg-slate-50" data-testid="incidents-management">
      {/* ─── Header ─────────────────────────────────────── */}
      <div className="px-6 py-4 border-b border-slate-200 bg-white sticky top-0 z-20">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={font.barlow}>Incidents</h1>
            <p className="text-xs text-slate-500 mt-0.5">Gestion des signalements et problèmes</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchQuery} onChange={e => { setSearchQuery(e.target.value); setPage(1); }}
                placeholder="Rechercher un incident..."
                className="pl-9 text-sm bg-slate-50 border-slate-200 focus:bg-white"
                data-testid="search-input"
              />
            </div>
            <div className="flex border border-slate-200 rounded-lg overflow-hidden">
              <button onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-slate-400 hover:text-slate-600'}`}
                data-testid="view-list-btn"><List className="w-4 h-4" /></button>
              <button onClick={() => setViewMode('kanban')}
                className={`p-2 ${viewMode === 'kanban' ? 'bg-emerald-50 text-emerald-700' : 'bg-white text-slate-400 hover:text-slate-600'}`}
                data-testid="view-kanban-btn"><Columns3 className="w-4 h-4" /></button>
            </div>
            <Button onClick={() => navigate('/admin/incidents/new')} className="bg-slate-800 hover:bg-slate-900 text-white font-medium shadow-sm"
              data-testid="new-incident-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Nouvel incident
            </Button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* ─── Main Content ──────────────────────────────── */}
        <div className={`flex-1 flex flex-col min-h-0 overflow-auto ${selectedIncident ? 'hidden md:flex' : 'flex'}`}>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-6 pb-3">
            <KPICard icon={AlertTriangle} label="Actifs" value={kpis.active} color="bg-slate-100 text-slate-600" />
            <KPICard icon={Zap} label="Urgents" value={kpis.urgent} color="bg-orange-50 text-orange-600" pulse={kpis.urgent > 0} />
            <KPICard icon={Timer} label="SLA depasses" value={kpis.slaOverdue} color="bg-slate-100 text-slate-600" pulse={kpis.slaOverdue > 0} />
            <KPICard icon={UserCheck} label="Non assignes" value={kpis.unassigned} color="bg-slate-100 text-slate-600" />
          </div>

          {/* Tabs */}
          <div className="px-6">
            <div className="flex items-center gap-1 overflow-x-auto pb-1">
              {TABS.map(t => (
                <button key={t.key} onClick={() => { setActiveTab(t.key); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors
                    ${activeTab === t.key ? 'bg-slate-200 text-slate-900' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
                  data-testid={`tab-${t.key}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 px-6 pb-6 pt-3 overflow-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : viewMode === 'kanban' ? (
              <KanbanBoard incidents={incidents} onSelect={selectIncident} />
            ) : (
              <div className="space-y-2">
                {incidents.length === 0 && (
                  <div className="text-center py-16">
                    <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-400">Aucun incident trouve</p>
                  </div>
                )}
                {incidents.map(inc => (
                  <IncidentRow key={inc.id} incident={inc} onClick={selectIncident}
                    isActive={selectedIncident?.id === inc.id} />
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t border-slate-200">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-xs text-slate-500" style={font.mono}>{page} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* ─── Detail Panel ──────────────────────────────── */}
        {selectedIncident && (
          <DetailPanel
            incident={selectedIncident}
            onClose={() => setSelectedIncident(null)}
            messages={messages}
            onSendMessage={handleSendMessage}
            internalNotes={internalNotes}
            onAddNote={handleAddNote}
            onTransition={handleTransition}
            onAssign={handleAssign}
            onCloseIncident={handleCloseIncident}
            agents={agents}
          />
        )}
      </div>

      {/* ─── Dialogs ─────────────────────────────────────── */}
      <TransitionDialog open={transitionOpen} onOpenChange={setTransitionOpen}
        incident={transitionIncident} targetStatus={transitionTarget} onConfirm={confirmTransition} />
      <CloseDialog open={closeOpen} onOpenChange={setCloseOpen}
        incident={closeIncident} onConfirm={confirmClose} />
    </div>
  );
};

export default IncidentsManagement;
